import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdManager from "@/components/AdManager";
import AdminStats from "@/components/AdminStats";
import AccountSettings from "@/components/AccountSettings";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Loader2, Eye } from "lucide-react";
import SEO from "@/components/SEO";
import { NewsletterManager } from "@/components/NewsletterManager";
import { YouTubeImporter } from "@/components/YouTubeImporter";
import { WorkNotes } from "@/components/WorkNotes";
import { toast } from "sonner";

export default function Admin() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [speeches, setSpeeches] = useState([]);
  const [reports, setReports] = useState([]);
  const [subs, setSubs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [speechFilter, setSpeechFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/speeches"),
      api.get("/admin/reports"),
      api.get("/admin/newsletter"),
      api.get("/admin/newsletter/campaigns"),
    ])
      .then(([s, r, n, c]) => {
        setSpeeches(s.data);
        setReports(r.data);
        setSubs(n.data);
        setCampaigns(c.data);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try {
      await api.delete(`/admin/speeches/${id}`);
      toast.success("Discorso eliminato");
      setSpeeches((prev) => prev.filter((s) => s.id !== id));
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const delSub = async (id) => {
    try {
      await api.delete(`/admin/newsletter/${id}`);
      toast.success("Iscritto eliminato");
      setSubs((prev) => prev.filter((n) => n.id !== id));
    } catch {
      toast.error("Errore nell'eliminazione dell'iscritto");
    }
  };

  const draftCount = speeches.filter((s) => s.status !== "published").length;
  const publishedCount = speeches.filter((s) => s.status === "published").length;
  const filteredSpeeches = speeches.filter((s) =>
    speechFilter === "all" ? true : speechFilter === "draft" ? s.status !== "published" : s.status === "published"
  );

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Area gestione" robots="noindex, nofollow, noarchive" suppressCanonical />
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur-xl z-40">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="brand-word text-base">Smart News Italia</Link>
            <Badge variant="secondary" className="rounded-sm">Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/login"); }} className="gap-1.5" data-testid="logout-btn">
              <LogOut className="h-4 w-4" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-3xl font-bold">Dashboard</h1>
          <Button onClick={() => navigate("/admin/nuovo")} className="rounded-sm gap-1.5" data-testid="new-speech-btn">
            <Plus className="h-4 w-4" /> Nuovo discorso
          </Button>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <Tabs defaultValue="speeches">
            <TabsList className="rounded-sm">
              <TabsTrigger value="stats" data-testid="tab-stats" className="rounded-sm">Statistiche</TabsTrigger>
              <TabsTrigger value="speeches" data-testid="tab-speeches" className="rounded-sm">Discorsi ({speeches.length})</TabsTrigger>
              <TabsTrigger value="import" data-testid="tab-import" className="rounded-sm">Importa</TabsTrigger>
              <TabsTrigger value="notes" data-testid="tab-notes" className="rounded-sm">In lavorazione</TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-sm">Segnalazioni ({reports.length})</TabsTrigger>
              <TabsTrigger value="subs" data-testid="tab-subs" className="rounded-sm">Newsletter ({subs.length})</TabsTrigger>
              <TabsTrigger value="storico" data-testid="tab-storico" className="rounded-sm">Storico invii ({campaigns.length})</TabsTrigger>
              <TabsTrigger value="ads" data-testid="tab-ads" className="rounded-sm">Pubblicità</TabsTrigger>
              <TabsTrigger value="account" data-testid="tab-account" className="rounded-sm">Account</TabsTrigger>
            </TabsList>

            <TabsContent value="stats" className="mt-6">
              <AdminStats />
            </TabsContent>

            <TabsContent value="speeches" className="mt-6">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {[
                  ["all", `Tutti (${speeches.length})`],
                  ["draft", `Bozze (${draftCount})`],
                  ["published", `Pubblicati (${publishedCount})`],
                ].map(([key, label]) => (
                  <Button
                    key={key}
                    size="sm"
                    variant={speechFilter === key ? "default" : "outline"}
                    className="rounded-sm"
                    onClick={() => setSpeechFilter(key)}
                    data-testid={`speech-filter-${key}`}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <div className="border border-border rounded-sm divide-y divide-border">
                {filteredSpeeches.length === 0 && <p className="p-6 text-muted-foreground text-sm">Nessun discorso in questa vista.</p>}
                {filteredSpeeches.map((s) => (
                  <div key={s.id} className="flex items-center gap-4 p-4" data-testid={`admin-speech-${s.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={s.status === "published" ? "default" : "secondary"} className="rounded-sm text-[10px]">
                          {s.status === "published" ? "Pubblicato" : "Bozza"}
                        </Badge>
                        {s.featured && <Badge variant="outline" className="rounded-sm text-[10px]">In evidenza</Badge>}
                        <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {s.views || 0}</span>
                      </div>
                      <p className="font-medium truncate">{s.title}</p>
                      <p className="text-xs text-muted-foreground">{s.leader} · {s.country} · {s.event_date}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {s.status === "published" && (
                        <Button asChild variant="ghost" size="icon" className="rounded-sm">
                          <Link to={`/discorso/${s.id}`} target="_blank"><ExternalLink className="h-4 w-4" /></Link>
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => navigate(`/admin/modifica/${s.id}`)} data-testid={`edit-${s.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-sm text-accent" data-testid={`delete-${s.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-sm">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare questo discorso?</AlertDialogTitle>
                            <AlertDialogDescription>L'azione è irreversibile.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(s.id)} className="rounded-sm bg-accent" data-testid={`confirm-delete-${s.id}`}>Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="import" className="mt-6">
              <YouTubeImporter onImported={load} />
            </TabsContent>

            <TabsContent value="notes" className="mt-6">
              <WorkNotes />
            </TabsContent>

            <TabsContent value="reports" className="mt-6">
              <div className="border border-border rounded-sm divide-y divide-border">
                {reports.length === 0 && <p className="p-6 text-muted-foreground text-sm">Nessuna segnalazione.</p>}                {reports.map((r) => (
                  <div key={r.id} className="p-4">
                    <p className="text-sm">{r.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">{r.email || "anonimo"} · {r.created_at?.slice(0, 10)}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="subs" className="mt-6">
              <NewsletterManager subsCount={subs.length} />
              <div className="border border-border rounded-sm divide-y divide-border">
                {subs.length === 0 && <p className="p-6 text-muted-foreground text-sm">Nessun iscritto.</p>}
                {subs.map((n) => (
                  <div key={n.id} className="p-3 flex items-center justify-between gap-3">
                    <span className="text-sm truncate">{n.name ? `${n.name} · ` : ""}{n.email}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge
                        variant={n.status === "confirmed" ? "default" : n.status === "unsubscribed" ? "destructive" : "secondary"}
                        className="rounded-sm text-[10px]"
                      >
                        {n.status === "confirmed" ? "Confermato" : n.status === "unsubscribed" ? "Disiscritto" : "In attesa"}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono">{n.created_at?.slice(0, 10)}</span>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" data-testid={`sub-delete-${n.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare questo iscritto?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {n.email} verrà rimosso definitivamente dalla lista. L'operazione non è annullabile.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => delSub(n.id)} data-testid={`sub-delete-confirm-${n.id}`}>Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="storico" className="mt-6">
              <div className="border border-border rounded-sm divide-y divide-border">
                {campaigns.length === 0 && <p className="p-6 text-muted-foreground text-sm">Nessuna newsletter inviata finora.</p>}
                {campaigns.map((c) => (
                  <div key={c.id} className="p-4 flex items-center justify-between gap-3" data-testid={`campaign-${c.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.subject}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{c.created_at?.slice(0, 16).replace("T", " ")}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="default" className="rounded-sm text-[10px]">{c.sent} recapitate</Badge>
                      {c.failed > 0 && <Badge variant="destructive" className="rounded-sm text-[10px]">{c.failed} non riuscite</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="ads" className="mt-6">
              <AdManager />
            </TabsContent>

            <TabsContent value="account" className="mt-6">
              <AccountSettings />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
